import * as React from "react";
import PropTypes from 'prop-types';
import { format, parseISO, isSameDay, addDays, subDays } from "date-fns";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Clock, ChevronLeft, ChevronRight, AlertCircle, RefreshCw, GripVertical } from "lucide-react";
import { formatRupees } from "../utils/currency";
import {
    TIMELINE_START_HOUR,
    TIMELINE_END_HOUR,
    HOUR_HEIGHT_PX,
    STATUS_COLORS,
    generateHourLabels,
    getAppointmentPosition
} from "@/utils/timelineUtils";
import { useTimelineInteractions } from "@/hooks/features/useTimelineInteractions";

// Custom hook for responsive breakpoint
const useIsLargeScreen = () => {
    const [isLarge, setIsLarge] = React.useState(false);

    React.useEffect(() => {
        const checkScreen = () => setIsLarge(window.innerWidth >= 1024);
        checkScreen();
        window.addEventListener('resize', checkScreen);
        return () => window.removeEventListener('resize', checkScreen);
    }, []);

    return isLarge;
};

export function TimelineView({
    appointments,
    allPatients,
    onAppointmentClick,
    selectedDate,
    setSelectedDate,
    onSlotClick,
    onAppointmentReschedule,
    onAppointmentResize,
    getAppointmentAccess
}) {
    const hourLabels = React.useMemo(() => generateHourLabels(), []);
    const isLargeScreen = useIsLargeScreen();

    // Use the logic hook!
    const {
        draggingAppointment,
        dragOverDate,
        dragOverY,
        isDragEnabled,
        isResizing,
        resizingAptId,
        resizeCurrentHeight,
        handleSlotClick,
        handleDragStart,
        handleDragOver,
        handleDragLeave,
        handleDrop,
        handleDragEnd,
        handleTouchStart,
        handleTouchMove,
        handleTouchEnd,
        handleResizeStart
    } = useTimelineInteractions({
        onAppointmentReschedule,
        onAppointmentResize,
        onSlotClick
    });

    // Generate dates to display (3 for large screens, 1 for small)
    const displayDates = React.useMemo(() => {
        if (isLargeScreen) {
            return [
                subDays(selectedDate, 1),
                selectedDate,
                addDays(selectedDate, 1)
            ];
        }
        return [selectedDate];
    }, [selectedDate, isLargeScreen]);

    // Group appointments by date
    const appointmentsByDate = React.useMemo(() => {
        const grouped = {};
        displayDates.forEach(date => {
            const dateKey = format(date, "yyyy-MM-dd");
            grouped[dateKey] = [];
        });

        if (appointments && Array.isArray(appointments)) {
            appointments.forEach(apt => {
                if (!apt.date_time) return;
                try {
                    const aptDate = parseISO(apt.date_time);
                    const dateKey = format(aptDate, "yyyy-MM-dd");
                    if (grouped[dateKey]) {
                        grouped[dateKey].push(apt);
                    }
                } catch {
                    // Skip invalid dates
                }
            });
        }

        // Sort each day's appointments
        Object.keys(grouped).forEach(key => {
            grouped[key].sort((a, b) => new Date(a.date_time) - new Date(b.date_time));
        });

        return grouped;
    }, [appointments, displayDates]);

    const goToPrevDay = () => {
        const newDate = new Date(selectedDate);
        newDate.setDate(newDate.getDate() - (isLargeScreen ? 3 : 1));
        setSelectedDate(newDate);
    };

    const goToNextDay = () => {
        const newDate = new Date(selectedDate);
        newDate.setDate(newDate.getDate() + (isLargeScreen ? 3 : 1));
        setSelectedDate(newDate);
    };

    const goToToday = () => {
        setSelectedDate(new Date());
    };

    const timelineHeight = (TIMELINE_END_HOUR - TIMELINE_START_HOUR + 1) * HOUR_HEIGHT_PX;
    const isToday = isSameDay(selectedDate, new Date());

    return (
        <div className="flex flex-col animate-in fade-in duration-500 overflow-hidden">
            {/* Date Navigation */}
            <div className="flex items-center justify-between mb-3 bg-white p-2 sm:p-3 rounded-xl border shadow-sm">
                <Button variant="ghost" size="sm" onClick={goToPrevDay} className="p-1 sm:p-2">
                    <ChevronLeft size={18} />
                </Button>
                <div className="flex items-center gap-2 sm:gap-3 flex-wrap justify-center">
                    <h2 className="text-sm sm:text-lg font-bold text-slate-800 text-center">
                        {isLargeScreen
                            ? `${format(displayDates[0], "MMM d")} - ${format(displayDates[2], "MMM d, yyyy")}`
                            : format(selectedDate, "EEE, MMM d")
                        }
                    </h2>
                    {!isToday && (
                        <Button variant="outline" size="sm" onClick={goToToday} className="text-xs h-7">
                            Today
                        </Button>
                    )}
                </div>
                <Button variant="ghost" size="sm" onClick={goToNextDay} className="p-1 sm:p-2">
                    <ChevronRight size={18} />
                </Button>
            </div>

            {/* Timeline Container */}
            <div className="border rounded-xl bg-white shadow-sm overflow-hidden">
                {/* Day Headers Row (for large screens) - OUTSIDE ScrollArea */}
                {isLargeScreen && (
                    <div className="flex border-b">
                        {/* Empty space for hour labels column */}
                        <div className="w-14 sm:w-16 flex-shrink-0 border-r bg-slate-50" />

                        {/* Day Headers */}
                        {displayDates.map((date) => {
                            const dateKey = format(date, "yyyy-MM-dd");
                            const isDateToday = isSameDay(date, new Date());

                            return (
                                <div
                                    key={dateKey}
                                    className={cn(
                                        "flex-1 px-2 py-2 text-center border-r last:border-r-0",
                                        isDateToday ? "bg-blue-50" : "bg-white"
                                    )}
                                >
                                    <span className="text-xs font-medium text-slate-500">
                                        {format(date, "EEE")}
                                    </span>
                                    <span className={cn(
                                        "block text-lg font-bold",
                                        isDateToday ? "text-blue-600" : "text-slate-800"
                                    )}>
                                        {format(date, "d")}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Scrollable Timeline */}
                <ScrollArea className="h-[420px] sm:h-[460px]">
                    <div className="flex min-w-0">
                        {/* Hour Labels Column */}
                        <div className="w-14 sm:w-16 flex-shrink-0 border-r bg-slate-50 sticky left-0 z-10">
                            {hourLabels.map(({ hour, label }) => (
                                <div
                                    key={hour}
                                    className="border-b border-slate-100 flex items-start justify-end pr-1 sm:pr-2 pt-0.5"
                                    style={{ height: HOUR_HEIGHT_PX }}
                                >
                                    <span className="text-[10px] sm:text-xs text-slate-500 font-medium">{label}</span>
                                </div>
                            ))}
                        </div>

                        {/* Day Columns */}
                        {displayDates.map((date) => {
                            const dateKey = format(date, "yyyy-MM-dd");
                            const dayAppointments = appointmentsByDate[dateKey] || [];
                            const isDateToday = isSameDay(date, new Date());
                            const isDropTarget = dragOverDate && isSameDay(dragOverDate, date);

                            return (
                                <div
                                    key={dateKey}
                                    data-day-column={dateKey}
                                    className={cn(
                                        "flex-1 min-w-[120px] sm:min-w-[200px] relative border-r last:border-r-0 cursor-pointer",
                                        isLargeScreen && "lg:min-w-0",
                                        isDropTarget && "bg-blue-50/50"
                                    )}
                                    style={{ height: timelineHeight }}
                                    onClick={(e) => handleSlotClick(e, date)}
                                    onDragOver={(e) => handleDragOver(e, date)}
                                    onDragLeave={handleDragLeave}
                                    onDrop={(e) => handleDrop(e, date)}
                                    onTouchMove={handleTouchMove}
                                    onTouchEnd={handleTouchEnd}
                                >
                                    {/* Hour Grid Lines */}
                                    {hourLabels.map(({ hour }) => (
                                        <div
                                            key={hour}
                                            className="absolute w-full border-b border-slate-100 pointer-events-none"
                                            style={{ top: (hour - TIMELINE_START_HOUR) * HOUR_HEIGHT_PX }}
                                        />
                                    ))}

                                    {/* Drop indicator */}
                                    {isDropTarget && dragOverY !== null && (
                                        <div
                                            className="absolute left-1 right-1 h-1 bg-blue-500 rounded z-40 pointer-events-none"
                                            style={{ top: dragOverY }}
                                        />
                                    )}

                                    {/* Current Time Indicator */}
                                    {isDateToday && (() => {
                                        const now = new Date();
                                        const currentHour = now.getHours();
                                        const currentMinute = now.getMinutes();
                                        if (currentHour >= TIMELINE_START_HOUR && currentHour <= TIMELINE_END_HOUR) {
                                            const top = ((currentHour - TIMELINE_START_HOUR) + (currentMinute / 60)) * HOUR_HEIGHT_PX;
                                            return (
                                                <div
                                                    className="absolute w-full flex items-center z-20 pointer-events-none"
                                                    style={{ top }}
                                                >
                                                    <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
                                                    <div className="flex-1 h-0.5 bg-red-500" />
                                                </div>
                                            );
                                        }
                                        return null;
                                    })()}

                                    {/* Appointment Blocks */}
                                    {dayAppointments.map((apt) => {
                                        const { top, height } = getAppointmentPosition(apt.date_time, apt.duration_minutes);
                                        const patient = allPatients.find(p => String(p.id) === String(apt.patient_id));
                                        const isAnonymized = apt.is_anonymized;
                                        // const doctor = allDoctors?.find(d => d.id === apt.doctor_id); // Unused
                                        const colors = STATUS_COLORS[apt.status] || STATUS_COLORS.scheduled;
                                        const isDragging = draggingAppointment?.id === apt.id;
                                        const access = getAppointmentAccess ? getAppointmentAccess(apt) : { canEdit: true, canViewFee: true };
                                        const canInteract = access.canEdit && !isAnonymized && apt.status === 'scheduled';

                                        // Use dynamic height if this appointment is being resized
                                        const isBeingResized = resizingAptId === apt.id;
                                        const displayHeight = isBeingResized ? resizeCurrentHeight : height;

                                        return (
                                            <div
                                                key={apt.id}
                                                data-appointment={apt.id}
                                                draggable={canInteract && !isResizing}
                                                className={cn(
                                                    "absolute left-1 right-1 rounded border-l-2 p-1 cursor-pointer transition-shadow hover:shadow-md hover:z-30 overflow-hidden text-xs select-none",
                                                    colors.bg,
                                                    colors.border,
                                                    isAnonymized && "opacity-75 cursor-default",
                                                    isDragging && "opacity-50 z-50",
                                                    isDragEnabled && draggingAppointment?.id === apt.id && "ring-2 ring-blue-500",
                                                    isBeingResized && "z-40 ring-2 ring-blue-400 shadow-lg"
                                                )}
                                                style={{
                                                    top: Math.max(top, 0),
                                                    height: Math.min(displayHeight, timelineHeight - top - 4),
                                                    transition: isBeingResized ? 'none' : undefined
                                                }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    // Don't navigate if currently resizing
                                                    if (isResizing) return;
                                                    if (!isAnonymized) onAppointmentClick(apt.id);
                                                }}
                                                onDragStart={(e) => {
                                                    if (!canInteract) {
                                                        e.preventDefault();
                                                        return;
                                                    }
                                                    handleDragStart(e, apt);
                                                }}
                                                onDragEnd={handleDragEnd}
                                                onTouchStart={(e) => {
                                                    if (!canInteract) return;
                                                    handleTouchStart(e, apt);
                                                }}
                                            >
                                                <div className="flex items-start justify-between h-full gap-1">
                                                    <div className="flex-1 min-w-0 overflow-hidden">
                                                        <span className={cn(
                                                            "font-medium truncate block text-[11px] sm:text-xs leading-tight flex items-center gap-0.5",
                                                            colors.text,
                                                            isAnonymized && "italic"
                                                        )}>
                                                            {isAnonymized
                                                                ? `Busy`
                                                                : (patient?.name || "Unknown")}
                                                            {!isAnonymized && apt.sync_status === 'failed' && (
                                                                <span title={apt.sync_error || 'Sync failed'} className="text-red-500 flex-shrink-0">
                                                                    <AlertCircle size={10} />
                                                                </span>
                                                            )}
                                                            {!isAnonymized && apt._synced === false && apt.sync_status !== 'failed' && (
                                                                <span title="Syncing..." className="text-amber-500 flex-shrink-0">
                                                                    <RefreshCw size={10} className="animate-spin" />
                                                                </span>
                                                            )}
                                                        </span>

                                                        {height >= 36 && !isAnonymized && (
                                                            <span className="text-[9px] text-slate-500 block truncate">
                                                                {format(parseISO(apt.date_time), "h:mm a")}
                                                            </span>
                                                        )}
                                                    </div>

                                                    {!isAnonymized && access.canViewFee && height >= 36 && !isLargeScreen && (
                                                        <span className={cn(
                                                            "text-[9px] font-medium whitespace-nowrap",
                                                            colors.text
                                                        )}>
                                                            {formatRupees(apt.fee_amount)}
                                                        </span>
                                                    )}
                                                </div>

                                                {/* Resize handle - only for scheduled appointments */}
                                                {canInteract && onAppointmentResize && displayHeight >= 24 && (
                                                    <div
                                                        className="absolute bottom-0 left-0 right-0 h-3 cursor-ns-resize flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity bg-gradient-to-t from-slate-300/70"
                                                        onMouseDown={(e) => handleResizeStart(e, apt)}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                        }}
                                                    >
                                                        <GripVertical size={10} className="text-slate-500 rotate-90" />
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}

                                    {/* Empty State (only for single day view) */}
                                    {!isLargeScreen && dayAppointments.length === 0 && (
                                        <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 pointer-events-none">
                                            <Clock className="w-8 h-8 mb-2 opacity-20" />
                                            <p className="text-sm font-medium">No appointments</p>
                                            <p className="text-xs opacity-60">Tap to add</p>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </ScrollArea>
            </div>
        </div>
    );
}

TimelineView.propTypes = {
    appointments: PropTypes.array,
    allPatients: PropTypes.array,
    onAppointmentClick: PropTypes.func,
    selectedDate: PropTypes.instanceOf(Date),
    setSelectedDate: PropTypes.func,
    onSlotClick: PropTypes.func,
    onAppointmentReschedule: PropTypes.func,
    onAppointmentResize: PropTypes.func,
    getAppointmentAccess: PropTypes.func
};
